/** Fully-local PDF text-layer parser and its legacy EffectParser adapter. */
import { Clock, Effect, Layer, Schema } from 'effect';

import { bytesRef, makeParserDependencyError, PdfTextLayer, type TextItem } from '../parser-host';
import {
  clipPageToRegion,
  defineDocumentParser,
  filterPagesByTag,
  makeParserError,
  runParsePage,
  type OfflineParser,
  type ParsePageInput,
  type ParsedPage,
  type ResolveByTagInput,
} from '../parser-layer';
import {
  defineEffectParser,
  type ParserInput,
  type ParserOutput,
  type ParserSpec,
} from '../parser';
import type { CanonicalBlock } from '../types';
import { toCanonicalParseResult } from '../projections/okra-parse-output';

const SPEC: ParserSpec = {
  id: 'textlayer',
  name: 'PDF Text Layer',
  version: '1.0.0',
  tier: 'fast',
  category: 'ocr',
  requiredEnvKeys: [],
  inputs: ['pdf-bytes'],
  outputs: ['parsed-result'],
  properties: [],
  bestFor: ['text-layer PDFs', 'court filings', 'emails', 'digital documents'],
  async: false,
};

const ExtractedTextSchema = Schema.Struct({
  totalPages: Schema.Finite,
  text: Schema.Array(Schema.String),
});

export interface TextLayerUnpdfModule {
  readonly getDocumentProxy: (bytes: Uint8Array) => Promise<unknown>;
  readonly extractText: (
    document: unknown,
    options: { readonly mergePages: false },
  ) => Promise<unknown>;
}

export interface TextLayerRuntimeServices {
  readonly loadUnpdf: () => Promise<TextLayerUnpdfModule>;
}

export type TextLayerOperation =
  | 'input'
  | 'load-unpdf'
  | 'document-proxy'
  | 'extract-text'
  | 'decode-result'
  | 'empty';

export class TextLayerParserError extends Schema.TaggedError<TextLayerParserError>()(
  'TextLayerParserError',
  {
    operation: Schema.Literals([
      'input',
      'load-unpdf',
      'document-proxy',
      'extract-text',
      'decode-result',
      'empty',
    ]),
    message: Schema.String,
    cause: Schema.optionalKey(Schema.Defect()),
  },
) {}

const defaultServices: TextLayerRuntimeServices = {
  async loadUnpdf() {
    const unpdf = await import('unpdf');
    return {
      async getDocumentProxy(bytes) {
        return unpdf.getDocumentProxy(bytes);
      },
      async extractText(document, options) {
        return unpdf.extractText(document as Parameters<typeof unpdf.extractText>[0], options);
      },
    };
  },
};

function boundaryTryPromise<A>(
  operation: TextLayerOperation,
  message: string,
  evaluate: () => Promise<A>,
): Effect.Effect<A, TextLayerParserError> {
  return Effect.tryPromise({
    try: evaluate,
    catch: (cause) => new TextLayerParserError({ operation, message, cause }),
  });
}

/** Established paragraph/header heuristic, shared by all text-layer surfaces. */
export function textLayerTextToBlocks(pageText: string): CanonicalBlock[] {
  const paragraphs = pageText
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
  return paragraphs.map((paragraph): CanonicalBlock => {
    const isHeader =
      paragraph.length < 120 &&
      (paragraph === paragraph.toUpperCase() ||
        /^(CHAPTER|SECTION|ARTICLE|EXHIBIT|APPENDIX)\b/i.test(paragraph));
    return {
      type: isHeader ? 'header' : 'text',
      ...(isHeader ? { label: paragraph } : {}),
      value: paragraph,
    };
  });
}

interface PositionedTextItem {
  readonly text: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly glyphHeight: number;
}

function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? ((sorted[middle - 1] ?? 0) + (sorted[middle] ?? 0)) / 2
    : (sorted[middle] ?? 0);
}

function clamp01(value: number): number {
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0;
}

function positionedItem(item: unknown): PositionedTextItem | undefined {
  if (!item || typeof item !== 'object') return undefined;
  const candidate = item as Partial<TextItem>;
  if (typeof candidate.str !== 'string' || !candidate.transform || candidate.transform.length < 6) {
    return undefined;
  }
  const text = candidate.str.replace(/\s+/g, ' ').trim();
  const x = Number(candidate.transform[4]);
  const y = Number(candidate.transform[5]);
  if (!text || !Number.isFinite(x) || !Number.isFinite(y)) return undefined;
  const transformHeight = Math.abs(Number(candidate.transform[3]));
  const fallbackHeight = typeof candidate.height === 'number' ? Math.abs(candidate.height) : 0;
  return {
    text,
    x,
    y,
    width:
      typeof candidate.width === 'number' && Number.isFinite(candidate.width)
        ? Math.max(0, candidate.width)
        : 0,
    glyphHeight: transformHeight > 0 ? transformHeight : fallbackHeight > 0 ? fallbackHeight : 1,
  };
}

/** Geometry-preserving segmentation used by the definition and server adapter. */
export function segmentTextLayerItemsIntoBlocks(
  items: readonly unknown[],
  pageWidth: number,
  pageHeight: number,
): CanonicalBlock[] {
  const positioned = items.flatMap((item) => {
    const value = positionedItem(item);
    return value ? [value] : [];
  });
  if (positioned.length === 0) return [];
  const medianLineHeight = Math.max(1, median(positioned.map((item) => item.glyphHeight)));
  const lineGroups: Array<{ referenceY: number; items: PositionedTextItem[] }> = [];
  for (const item of [...positioned].sort((left, right) => right.y - left.y)) {
    const current = lineGroups.at(-1);
    if (!current || Math.abs(item.y - current.referenceY) > medianLineHeight * 0.5) {
      lineGroups.push({ referenceY: item.y, items: [item] });
    } else current.items.push(item);
  }
  const lines = lineGroups
    .map((group) => {
      const lineItems = [...group.items].sort((left, right) => left.x - right.x);
      return {
        baselineY: median(lineItems.map((item) => item.y)),
        items: lineItems,
        text: lineItems
          .map((item) => item.text)
          .join(' ')
          .replace(/\s+/g, ' ')
          .trim(),
      };
    })
    .filter((line) => line.text)
    .sort((left, right) => right.baselineY - left.baselineY);
  const blocks: Array<typeof lines> = [];
  for (const line of lines) {
    const current = blocks.at(-1);
    const previous = current?.at(-1);
    if (!current || (previous ? previous.baselineY - line.baselineY : 0) > medianLineHeight * 1.5) {
      blocks.push([line]);
    } else current.push(line);
  }
  const width = Number.isFinite(pageWidth) && pageWidth > 0 ? pageWidth : 1;
  const height = Number.isFinite(pageHeight) && pageHeight > 0 ? pageHeight : 1;
  return blocks.map((blockLines) => {
    const blockItems = blockLines.flatMap((line) => line.items);
    const left = Math.min(...blockItems.map((item) => item.x));
    const right = Math.max(...blockItems.map((item) => item.x + item.width));
    const top = Math.max(...blockItems.map((item) => item.y + item.glyphHeight));
    const bottom = Math.min(...blockItems.map((item) => item.y));
    const value = blockLines.map((line) => line.text).join('\n');
    const [base] = textLayerTextToBlocks(value);
    return {
      type: base?.type ?? 'text',
      ...(base?.label === undefined ? {} : { label: base.label }),
      value,
      bbox: {
        x: clamp01(left / width),
        y: clamp01((height - top) / height),
        w: clamp01((right - left) / width),
        h: clamp01((top - bottom) / height),
      },
    } satisfies CanonicalBlock;
  });
}

function pageText(items: readonly TextItem[]): string {
  return items
    .map((item) => item.str)
    .join('')
    .trim();
}

const parseTextLayerPageEffect = Effect.fn('okrapdf/parser-sdk.textLayer.parsePage')(function* (
  input: ParsePageInput,
) {
  const textLayer = yield* PdfTextLayer;
  const extracted = yield* textLayer.extractPage(input.document, input.page).pipe(
    Effect.mapError((cause) =>
      makeParserError(
        {
          parserId: 'textlayer',
          page: input.page,
          code: cause.code,
          retryable: cause.retryable,
          message: 'failed to extract the PDF text layer',
        },
        cause,
      ),
    ),
  );
  const raw = pageText(extracted.items);
  const positioned = segmentTextLayerItemsIntoBlocks(
    extracted.items,
    extracted.width,
    extracted.height,
  );
  return {
    page: input.page,
    width: extracted.width,
    height: extracted.height,
    blocks: positioned.length > 0 ? positioned : textLayerTextToBlocks(raw),
    raw,
  } satisfies ParsedPage;
});

/** The offline authoring contract. Its inferred requirement is exactly PdfTextLayer. */
export const textLayerParser = defineDocumentParser({
  spec: { ...SPEC, provides: ['text', 'layout', 'bbox'], network: 'none' as const },
  available: Effect.succeed(true),
  parsePage: parseTextLayerPageEffect,
  extractRegion: Effect.fn('okrapdf/parser-sdk.textLayer.extractRegion')(function* (input) {
    const page = yield* parseTextLayerPageEffect(input);
    return clipPageToRegion(page, input.region);
  }),
  resolveByTag: Effect.fn('okrapdf/parser-sdk.textLayer.resolveByTag')(function* (
    input: ResolveByTagInput,
  ) {
    const pages = yield* Effect.forEach(input.pages, (page) =>
      parseTextLayerPageEffect({ document: input.document, page }),
    );
    return filterPagesByTag(pages, input.tag);
  }),
});

// Compile-time proof maintained next to the definition.
const offlineTextLayerParser: OfflineParser<typeof textLayerParser> = textLayerParser;
void offlineTextLayerParser;

/** Native legacy whole-document program, now projecting pages through the definition. */
export const parseTextLayer = Effect.fn('okrapdf/parser-sdk.parseTextLayer')(function* (
  input: ParserInput,
  services: TextLayerRuntimeServices = defaultServices,
): Effect.fn.Return<ParserOutput, TextLayerParserError> {
  if (!input.pdfBytes) {
    return yield* new TextLayerParserError({
      operation: 'input',
      message: 'textlayer requires pdfBytes input',
    });
  }
  const startedAt = yield* Clock.currentTimeMillis;
  const unpdf = yield* boundaryTryPromise(
    'load-unpdf',
    'failed to load the local PDF text extractor',
    () => services.loadUnpdf(),
  );
  const pdf = yield* boundaryTryPromise('document-proxy', 'failed to open the PDF text layer', () =>
    unpdf.getDocumentProxy(new Uint8Array(input.pdfBytes!)),
  );
  const rawExtraction = yield* boundaryTryPromise(
    'extract-text',
    'failed to extract the PDF text layer',
    () => unpdf.extractText(pdf, { mergePages: false }),
  );
  const extraction = yield* Schema.decodeUnknownEffect(ExtractedTextSchema)(rawExtraction).pipe(
    Effect.mapError(
      (cause) =>
        new TextLayerParserError({
          operation: 'decode-result',
          message: 'the PDF text extractor returned an invalid result',
          cause,
        }),
    ),
  );
  const totalChars = extraction.text.reduce((sum, text) => sum + text.trim().length, 0);
  if (totalChars < 20) {
    return yield* new TextLayerParserError({
      operation: 'empty',
      message:
        `PDF has no meaningful text layer (${totalChars} chars across ${extraction.totalPages} pages). ` +
        'Use an OCR parser (llamaparse, dots-ocr) for scanned documents.',
    });
  }
  const host = Layer.succeed(
    PdfTextLayer,
    PdfTextLayer.of({
      extractPage: (_document, page) => {
        const text = extraction.text[page - 1];
        return text === undefined
          ? Effect.fail(
              makeParserDependencyError({
                service: 'text-layer',
                operation: 'extract-page',
                code: 'invalid_page',
                retryable: false,
              }),
            )
          : Effect.succeed({ items: [{ str: text }], width: 1, height: 1 });
      },
    }),
  );
  const pages = yield* Effect.forEach(
    extraction.text.map((_, index) => index + 1),
    (page) =>
      runParsePage(
        textLayerParser,
        {
          document: bytesRef({
            documentId: input.context?.documentId ?? 'legacy:textlayer',
            bytes: new Uint8Array(input.pdfBytes!),
            pageCount: extraction.totalPages,
          }),
          page,
        },
        host,
      ).pipe(
        Effect.mapError(
          (cause) =>
            new TextLayerParserError({
              operation: 'extract-text',
              message: 'failed to project the PDF text layer',
              cause,
            }),
        ),
      ),
  );
  const durationMs = (yield* Clock.currentTimeMillis) - startedAt;
  const legacyPages = pages.map(({ width: _width, height: _height, ...page }) => ({
    ...page,
    blocks: page.blocks.map((block) => ({ ...block, label: block.label })),
  }));
  return {
    parsedResult: toCanonicalParseResult(legacyPages, textLayerParser.spec, { durationMs }),
    rawPages: pages.map((page) => ({ pageNumber: page.page, raw: page.raw })),
    durationMs,
  };
});

/** @deprecated Author new integrations against textLayerParser. */
export const textlayerParser = defineEffectParser({
  spec: SPEC,
  parseEffect: (input) => parseTextLayer(input),
});
